import { useState, useEffect, useMemo } from "react";
import {
  Search,
  Mic,
  Video,
  AudioLines,
  ArrowLeft,
  Clock,
  Users,
  ListChecks,
  Loader2,
  MessageSquare,
  FileText,
  Sparkles,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { TranscriptChat } from "@/components/transcripts/TranscriptChat";
import {
  useTranscripts,
  useTranscript,
  transcriptDisplayTitle,
  formatDuration,
  cleanSummaryText,
  type Transcript,
} from "@/hooks/useTranscripts";
import { cn } from "@/lib/utils";

const SOURCE_META: Record<string, { label: string; icon: typeof Mic }> = {
  plaud: { label: "Plaud", icon: Mic },
  fireflies: { label: "Fireflies", icon: Video },
  otter: { label: "Otter", icon: AudioLines },
  google_meet: { label: "Meet", icon: Video },
};

function SourceBadge({ source }: { source: string }) {
  const meta = SOURCE_META[source] ?? { label: source, icon: AudioLines };
  const Icon = meta.icon;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide shrink-0">
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

function TranscriptRow({
  t,
  onClick,
}: {
  t: Transcript;
  onClick: () => void;
}) {
  const date = t.meeting_date ? new Date(t.meeting_date) : null;
  const duration = formatDuration(t.duration_seconds);
  const actionCount = Array.isArray(t.action_items) ? t.action_items.length : 0;
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-card border border-border rounded-xl px-4 py-3 hover:border-primary/40 transition-colors"
    >
      <div className="flex items-center gap-2 mb-1">
        <SourceBadge source={t.source} />
        <span className="text-xs text-muted-foreground shrink-0">
          {date
            ? date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
            : "—"}
        </span>
        {duration && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground shrink-0">
            <Clock className="h-3 w-3" />
            {duration}
          </span>
        )}
        {actionCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground ml-auto shrink-0">
            <ListChecks className="h-3 w-3" />
            {actionCount}
          </span>
        )}
      </div>
      <div className="font-medium text-sm truncate">{transcriptDisplayTitle(t)}</div>
      {t.summary && (
        <div className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {cleanSummaryText(t.summary)}
        </div>
      )}
      {t.speakers && t.speakers.length > 0 && (
        <div className="flex items-center gap-1 text-[11px] text-muted-foreground mt-1.5 truncate">
          <Users className="h-3 w-3 shrink-0" />
          <span className="truncate">{t.speakers.join(", ")}</span>
        </div>
      )}
    </button>
  );
}

type DetailTab = "overview" | "transcript" | "chat";

function TranscriptDetailView({
  id,
  onBack,
}: {
  id: string;
  onBack: () => void;
}) {
  const { data: t, isLoading } = useTranscript(id);
  const [tab, setTab] = useState<DetailTab>("overview");

  if (isLoading || !t) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const date = t.meeting_date ? new Date(t.meeting_date) : null;
  const duration = formatDuration(t.duration_seconds);
  const actionItems = Array.isArray(t.action_items) ? (t.action_items as string[]) : [];

  const tabs: Array<{ id: DetailTab; label: string; icon: typeof FileText }> = [
    { id: "overview", label: "Overview", icon: Sparkles },
    { id: "transcript", label: "Transcript", icon: FileText },
    { id: "chat", label: "Ask AI", icon: MessageSquare },
  ];

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="shrink-0 pb-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          All transcripts
        </button>
        <div className="flex items-start gap-2">
          <h1 className="text-lg font-semibold leading-snug flex-1 min-w-0">
            {transcriptDisplayTitle(t)}
          </h1>
          <SourceBadge source={t.source} />
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1 flex-wrap">
          {date && (
            <span>
              {date.toLocaleDateString(undefined, {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}{" "}
              {date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
            </span>
          )}
          {duration && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {duration}
            </span>
          )}
          {t.speakers && t.speakers.length > 0 && (
            <span className="inline-flex items-center gap-1 min-w-0">
              <Users className="h-3 w-3 shrink-0" />
              <span className="truncate">{t.speakers.join(", ")}</span>
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="shrink-0 flex gap-1 border-b border-border">
        {tabs.map(({ id: tabId, label, icon: Icon }) => (
          <button
            key={tabId}
            onClick={() => setTab(tabId)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab === tabId
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === "overview" && (
          <div className="h-full overflow-y-auto py-4 space-y-5">
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Summary
              </h2>
              {t.summary ? (
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {cleanSummaryText(t.summary)}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">No summary yet.</p>
              )}
            </section>
            <section>
              <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Action items
              </h2>
              {actionItems.length > 0 ? (
                <ul className="space-y-1.5">
                  {actionItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <ListChecks className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      <span>{typeof item === "string" ? item : JSON.stringify(item)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No action items captured.</p>
              )}
            </section>
          </div>
        )}

        {tab === "transcript" && (
          <div className="h-full overflow-y-auto py-4">
            {t.transcript_text ? (
              <pre className="text-sm leading-relaxed whitespace-pre-wrap font-sans">
                {t.transcript_text}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">
                Full transcript text isn't stored for this recording.
              </p>
            )}
          </div>
        )}

        {tab === "chat" && <TranscriptChat transcript={t} />}
      </div>
    </div>
  );
}

export default function Transcripts() {
  const [search, setSearch] = useState("");
  const [source, setSource] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { data: transcripts, isLoading } = useTranscripts(search, source);

  // Mobile back gesture returns to the list instead of leaving the page
  // (same history.pushState pattern as Messages conversations).
  useEffect(() => {
    const onPop = () => setSelectedId(null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const openTranscript = (id: string) => {
    window.history.pushState({ transcript: id }, "");
    setSelectedId(id);
  };

  const closeTranscript = () => {
    setSelectedId(null);
    if (window.history.state?.transcript) window.history.back();
  };

  const sources = useMemo(() => {
    const present = new Set((transcripts || []).map((t) => t.source));
    return ["plaud", "fireflies", "otter", "google_meet"].filter((s) => present.has(s) || source === s);
  }, [transcripts, source]);

  return (
    <AppLayout>
      {selectedId ? (
        <div className="h-full max-w-3xl mx-auto">
          <TranscriptDetailView id={selectedId} onBack={closeTranscript} />
        </div>
      ) : (
        <div className="max-w-3xl mx-auto space-y-3">
          {/* Search + filters */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search transcripts..."
                className="w-full bg-muted rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          {sources.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <button
                onClick={() => setSource(null)}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                  source === null
                    ? "bg-primary/10 border-primary/40 text-primary"
                    : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                All
              </button>
              {sources.map((s) => (
                <button
                  key={s}
                  onClick={() => setSource(source === s ? null : s)}
                  className={cn(
                    "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                    source === s
                      ? "bg-primary/10 border-primary/40 text-primary"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )}
                >
                  {SOURCE_META[s]?.label ?? s}
                </button>
              ))}
            </div>
          )}

          {/* List */}
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (transcripts || []).length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground">
              {search || source
                ? "No transcripts match."
                : "No transcripts yet. Connect Plaud or Fireflies in Settings and they'll land here."}
            </div>
          ) : (
            <div className="space-y-2 pb-4">
              {(transcripts || []).map((t) => (
                <TranscriptRow key={t.id} t={t} onClick={() => openTranscript(t.id)} />
              ))}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
